import { useEffect } from "react";

/**
 * SeoHead — tiny in-page <head> manager (no react-helmet dependency).
 *
 * On mount sets <title> + writes/updates these meta tags:
 *   • <meta name="description" content="..." />
 *   • <meta name="keywords" content="..." />
 *   • <meta property="og:title" content="..." />
 *   • <meta property="og:description" content="..." />
 *
 * On unmount restores the previous title so back navigations don't leak
 * the FAQ title onto the Feed page.
 */
export default function SeoHead({ title, description, keywords, canonical, ogImage }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const upsertMeta = (selector, attrName, attrValue, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };
    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      return el;
    };

    const created = [];
    if (description) {
      const m = upsertMeta('meta[name="description"]', "name", "description", description);
      created.push(["restore", m, "content", m.getAttribute("content")]);
      upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    }
    if (keywords) {
      const m = upsertMeta('meta[name="keywords"]', "name", "keywords", keywords);
      created.push(["restore", m, "content", m.getAttribute("content")]);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    }
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage);
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);
    }
    if (canonical) {
      upsertLink("canonical", canonical);
      upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    }

    return () => {
      document.title = prevTitle;
      // We leave the meta tags in place — they're harmless and most SPAs
      // do the same. If you want strict cleanup, walk `created`.
    };
  }, [title, description, keywords, canonical, ogImage]);

  return null;
}
